import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import type { DynamicEventSocket } from '../level/segments/SegmentDefinition';
import type { SegmentInstance } from '../level/segments/SegmentInstance';
import type { Aabb } from '../physics/Aabb';
import type { DynamicEventType } from './DynamicEventType';

/** Runtime state of one dynamic event socket in the running track. */
export interface DynamicEventSocketInstance {
  readonly segment: SegmentInstance;
  readonly socket: DynamicEventSocket;
  /** Track distance of the socket. */
  readonly distance: number;
  armed: boolean;
}

/**
 * Behaviour of a concrete event (forklift crossing, crane load swing, …).
 * Prepared interface – no behaviours are registered yet.
 */
export interface DynamicEventBehavior {
  readonly type: DynamicEventType;
  /** Called when the player reaches the socket's trigger distance. */
  start(socket: DynamicEventSocketInstance): void;
  /** Advances the event; returns false when finished. */
  update(dt: number): boolean;
  /** Track-space colliders the event currently occupies (gameplay hazard). */
  colliders(): readonly Aabb[];
  dispose(): void;
}

export type DynamicEventFactory = () => DynamicEventBehavior;

/**
 * Tracks dynamic event sockets of active segments and arms them when the
 * player approaches. Behaviours are registered per event type later; until
 * then sockets are only armed (visible in the debug view).
 */
export class DynamicEventManager {
  private readonly sockets = new Map<number, DynamicEventSocketInstance[]>();
  private readonly factories = new Map<DynamicEventType, DynamicEventFactory>();
  private readonly running: { readonly behavior: DynamicEventBehavior; readonly segmentUid: number }[] = [];

  constructor(private readonly events: EventBus<GameEvents>) {
    events.on('segment:spawned', ({ segment }) => {
      this.sockets.set(
        segment.uid,
        segment.definition.dynamicEventSockets.map((socket) => ({
          segment,
          socket,
          distance: segment.localToTrack(socket.position).z,
          armed: false,
        })),
      );
    });
    events.on('segment:recycled', ({ segment }) => {
      this.sockets.delete(segment.uid);
      // A behaviour must never outlive the segment that hosts it.
      for (let i = this.running.length - 1; i >= 0; i--) {
        const entry = this.running[i];
        if (entry?.segmentUid !== segment.uid) continue;
        entry.behavior.dispose();
        this.running.splice(i, 1);
      }
    });
  }

  register(type: DynamicEventType, factory: DynamicEventFactory): void {
    this.factories.set(type, factory);
  }

  activeSockets(): DynamicEventSocketInstance[] {
    return [...this.sockets.values()].flat();
  }

  /**
   * Colliders of running behaviours (none registered). The moving hazards of phase 2 do not
   * use behaviours: HazardManager listens to `dynamicEvent:armed` and feeds the CollisionSystem.
   */
  hazardColliders(): Aabb[] {
    return this.running.flatMap((entry) => [...entry.behavior.colliders()]);
  }

  update(dt: number, playerDistance: number): void {
    for (const list of this.sockets.values()) {
      for (const instance of list) {
        if (instance.armed || !instance.segment.committed) continue;
        if (playerDistance < instance.distance - instance.socket.triggerDistance) continue;
        instance.armed = true;
        this.events.emit('dynamicEvent:armed', { socket: instance });
        // Event selection (type, probability, difficulty) will live here.
        const factory = instance.socket.supportedEvents.map((t) => this.factories.get(t)).find(Boolean);
        if (factory) {
          const behavior = factory();
          behavior.start(instance);
          this.running.push({ behavior, segmentUid: instance.segment.uid });
        }
      }
    }
    for (let i = this.running.length - 1; i >= 0; i--) {
      const entry = this.running[i];
      if (entry && !entry.behavior.update(dt)) {
        entry.behavior.dispose();
        this.running.splice(i, 1);
      }
    }
  }

  reset(): void {
    for (const entry of this.running) entry.behavior.dispose();
    this.running.length = 0;
  }
}
